/**
 * Crash-recovery for actor-side BWF takes (rt UI v2 — closes the S5 gap).
 *
 * The OPFS sink (recorder-bridge → BwfSessionRecorder → OpfsSink) makes bytes
 * durable every ~3 s, so a crashed/killed tab leaves a playable .wav (header
 * sizes patched on the same tick) in origin-private storage. This module is
 * deliberately INDEPENDENT of recorder-bridge (its public API is frozen):
 *
 *   scanOpfsRecordings()         list surviving *.wav + parse header/T##
 *   uploadRecovered(token, rec)  re-run the frozen upload contract
 *                                (register → parts presign/PUT → complete)
 *   discardRecovered(rec)        delete .wav (+ .json sidecar) from OPFS
 *
 * Uploaded files are removed from OPFS on success so the recovery card does
 * not re-offer them on the next join.
 */
import { completeTake, presignTakePart, registerTake } from '../lib/api'

export interface RecoveredRecording {
  fileName: string
  /** fileName minus .wav */
  fileBase: string
  bytes: number
  /** parsed from the data chunk (header patched every 3 s); null if unparseable */
  durationSec: number | null
  /** parsed from the _T## _ file-name segment */
  take: number | null
  /** bext TimeReference (samples since midnight @48k); null if unparseable */
  tcStartSamples: number | null
  sampleRate: number
  bitDepth: number
  channels: number
  /** sibling .json sidecar content, if the recorder got far enough to write it */
  sidecar: Record<string, unknown> | null
}

export type RecoveryUploadPhase = 'registering' | 'uploading' | 'uploaded' | 'failed'

const PART_BYTES = 10 * 1024 * 1024

interface WavHeaderInfo {
  sampleRate: number
  bitDepth: number
  channels: number
  blockAlign: number
  dataBytes: number | null
  dataOffset: number | null
  timeReference: number | null
}

/** Minimal RIFF walker over the first slice of the file (fmt + bext + data). */
function parseWavHeader(buf: ArrayBuffer): WavHeaderInfo | null {
  const u8 = new Uint8Array(buf)
  const dv = new DataView(buf)
  const fourcc = (o: number) =>
    String.fromCharCode(u8[o] ?? 0, u8[o + 1] ?? 0, u8[o + 2] ?? 0, u8[o + 3] ?? 0)
  if (u8.length < 12 || fourcc(0) !== 'RIFF' || fourcc(8) !== 'WAVE') return null
  const out: WavHeaderInfo = {
    sampleRate: 48000,
    bitDepth: 24,
    channels: 1,
    blockAlign: 3,
    dataBytes: null,
    dataOffset: null,
    timeReference: null,
  }
  let o = 12
  while (o + 8 <= u8.length) {
    const id = fourcc(o)
    const size = dv.getUint32(o + 4, true)
    const body = o + 8
    if (id === 'fmt ' && body + 16 <= u8.length) {
      out.channels = dv.getUint16(body + 2, true)
      out.sampleRate = dv.getUint32(body + 4, true)
      out.blockAlign = dv.getUint16(body + 12, true)
      out.bitDepth = dv.getUint16(body + 14, true)
    } else if (id === 'bext' && body + 346 <= u8.length) {
      const lo = dv.getUint32(body + 338, true)
      const hi = dv.getUint32(body + 342, true)
      out.timeReference = hi * 4294967296 + lo
    } else if (id === 'data') {
      out.dataBytes = size
      out.dataOffset = body
      break // PCM follows — stop walking
    }
    o = body + size + (size % 2)
  }
  return out
}

async function opfsRoot(): Promise<FileSystemDirectoryHandle | null> {
  if (typeof navigator === 'undefined' || typeof navigator.storage?.getDirectory !== 'function') {
    return null
  }
  try {
    return await navigator.storage.getDirectory()
  } catch {
    return null
  }
}

// OPFS directory iteration is not yet in every TS dom lib — minimal typing.
interface IterableDirHandle extends FileSystemDirectoryHandle {
  entries(): AsyncIterableIterator<[string, FileSystemHandle]>
}

/**
 * List surviving .wav recordings in OPFS. Call ON MOUNT, before the current
 * session records anything — fresh takes land in OPFS too and would otherwise
 * be misreported (they are excluded simply by scanning first).
 */
export async function scanOpfsRecordings(): Promise<RecoveredRecording[]> {
  const dir = await opfsRoot()
  if (!dir) return []
  const found: RecoveredRecording[] = []
  const sidecars = new Map<string, FileSystemFileHandle>()
  const wavs: FileSystemFileHandle[] = []
  try {
    for await (const [name, handle] of (dir as IterableDirHandle).entries()) {
      if (handle.kind !== 'file') continue
      if (name.toLowerCase().endsWith('.wav')) wavs.push(handle as FileSystemFileHandle)
      else if (name.toLowerCase().endsWith('.json')) {
        sidecars.set(name.replace(/\.json$/i, ''), handle as FileSystemFileHandle)
      }
    }
  } catch {
    return []
  }

  for (const handle of wavs) {
    try {
      const file = await handle.getFile()
      if (file.size < 100) continue // empty/garbage stub — not recoverable
      const head = parseWavHeader(await file.slice(0, 4096).arrayBuffer())
      if (!head) continue
      const fileBase = file.name.replace(/\.wav$/i, '')
      // Header data size patched every 3 s; clamp to what is actually on disk.
      const onDisk = head.dataOffset != null ? Math.max(0, file.size - head.dataOffset) : null
      const dataBytes =
        head.dataBytes != null && onDisk != null ? Math.min(head.dataBytes, onDisk) : onDisk
      const bytesPerSec = head.sampleRate * head.blockAlign
      const takeMatch = /_T(\d{2,3})_/.exec(file.name)

      let sidecar: Record<string, unknown> | null = null
      const sc = sidecars.get(fileBase)
      if (sc) {
        try {
          sidecar = JSON.parse(await (await sc.getFile()).text()) as Record<string, unknown>
        } catch {
          sidecar = null
        }
      }

      found.push({
        fileName: file.name,
        fileBase,
        bytes: file.size,
        durationSec:
          dataBytes != null && bytesPerSec > 0
            ? Math.round((dataBytes / bytesPerSec) * 10) / 10
            : null,
        take: takeMatch?.[1] ? Number.parseInt(takeMatch[1], 10) : null,
        tcStartSamples: head.timeReference,
        sampleRate: head.sampleRate,
        bitDepth: head.bitDepth,
        channels: head.channels,
        sidecar,
      })
    } catch {
      /* unreadable entry — skip */
    }
  }
  found.sort((a, b) => a.fileName.localeCompare(b.fileName))
  return found
}

/** One retry on failure (same v1 policy as the live upload pipeline). */
async function retryOnce<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn()
  } catch {
    return await fn()
  }
}

/**
 * Re-upload a survivor through the frozen take contract. On success the OPFS
 * .wav/.json are deleted. Progress: 0..95 parts, 100 complete.
 */
export async function uploadRecovered(
  linkToken: string,
  rec: RecoveredRecording,
  onProgress?: (phase: RecoveryUploadPhase, pct: number) => void,
): Promise<void> {
  const dir = await opfsRoot()
  if (!dir) throw new Error('OPFS unavailable')
  const handle = await dir.getFileHandle(rec.fileName)
  const file = await handle.getFile()

  onProgress?.('registering', 0)
  const reg = await retryOnce(() =>
    registerTake(linkToken, {
      take_number: rec.take ?? 1,
      filename: rec.fileName,
      tc_start_samples: rec.tcStartSamples ?? 0,
      sample_rate: rec.sampleRate,
      bit_depth: rec.bitDepth,
      channels: rec.channels,
      ...(rec.sidecar ? { sidecar: rec.sidecar } : {}),
    }),
  )

  onProgress?.('uploading', 0)
  const total = file.size
  const parts: Array<{ n: number; etag: string }> = []
  for (let n = 1, offset = 0; offset < total; n += 1, offset += PART_BYTES) {
    const slice = file.slice(offset, Math.min(offset + PART_BYTES, total))
    const { url } = await retryOnce(() => presignTakePart(linkToken, reg.take_id, n, reg.upload_id))
    const res = await retryOnce(async () => {
      const r = await fetch(url, { method: 'PUT', body: slice })
      if (!r.ok) throw new Error(`part ${n} PUT failed (${r.status})`)
      return r
    })
    const etag = res.headers.get('etag')
    if (!etag) throw new Error(`part ${n}: ETag not exposed (check bucket CORS)`)
    parts.push({ n, etag })
    onProgress?.('uploading', Math.round((Math.min(offset + PART_BYTES, total) / total) * 95))
  }

  await retryOnce(() =>
    completeTake(linkToken, reg.take_id, {
      upload_id: reg.upload_id,
      parts,
      ...(rec.durationSec != null ? { duration_sec: rec.durationSec } : {}),
      size_bytes: total,
      ...(rec.sidecar ? { sidecar: rec.sidecar } : {}),
    }),
  )
  onProgress?.('uploaded', 100)

  // Success → clear the survivor so the next join doesn't re-offer it.
  await discardRecovered(rec).catch(() => undefined)
}

/** Delete a survivor (.wav + .json sidecar) from OPFS. */
export async function discardRecovered(rec: RecoveredRecording): Promise<void> {
  const dir = await opfsRoot()
  if (!dir) return
  await dir.removeEntry(rec.fileName).catch(() => undefined)
  await dir.removeEntry(`${rec.fileBase}.json`).catch(() => undefined)
}
